import {
  getIsNotificationSoundOn,
  getSoundNotificationSetting,
} from 'conversations/modules/notifications/selectors'
import { getAppServices } from 'conversations/services'
import { captureError } from 'conversations/utils/logRocket'
import { Epic } from 'redux-observable'
import { empty, from } from 'rxjs'
import { catchError, filter, mergeMap, pluck } from 'rxjs/operators'
import { NotificationBase } from 'shared/notifications/ConversationNotification'
import { DesktopNotificationPayloadBase } from 'shared/src/notifications/DesktopPushNotification'
import { receiveNotification } from 'shared/store/actions/notifications'

import { isActionOf, RootAction, RootState } from 'typesafe-actions'

import { getConversationIdFromPathName } from '../conversation/selectors'

import * as api from './api'

export interface DriftNotification {
  requireInteraction?: boolean
  useBodyForGroupingText?: boolean
  body?: string
  title?: string
  groupingText?: string
  conversationId?: number
  endUserId?: number
  messageId?: number
  onClickUrl?: string
  tracking?: TrackingPayload
}

export interface TrackingPayload {
  key?: string //Segment
  userId?: string //Segment
  trackingUrl?: string //Segment
  auditLogTrackingUrl?: string
  driftUserId: string
  auditLogSortKey: string
  eventType: string
  timestamp: string
  permissionGranted?: boolean
}

export interface TrackingInfo {
  permissionGranted?: boolean
}

const buildNotification = (
  state: RootState,
  title: string,
  body: string,
  onClickUrl: string
): NotificationBase => {
  const sound = getSoundNotificationSetting(state)
  const soundOn = getIsNotificationSoundOn(state)

  return {
    title,
    body,
    soundOn,
    sound,
    onClickUrl,
  }
}

// eslint-disable-next-line
const logNotificationToAuditLog = (notification: DesktopNotificationPayloadBase, api: any) =>
  from(api.updateNotificationsAuditLog(notification)).pipe(mergeMap(() => empty()))

export const receiveNotificationEpic: Epic<RootAction, RootAction, RootState> = (
  action$,
  state
) => {
  return action$.pipe(
    filter(isActionOf(receiveNotification)),
    pluck('payload'),
    mergeMap((notification) =>
      from(getAppServices().badges.isWindowVisible()).pipe(
        mergeMap((isVisible) => {
          const currentConversationId = getConversationIdFromPathName()
          if (!isVisible || currentConversationId !== notification.body.message.conversationId) {
            const message = notification.body.message
            const baseNotification = buildNotification(
              state.value,
              message.title,
              message.body || '',
              message.onClickUrl
            )

            getAppServices().notifications.sendNotification({
              ...baseNotification,
              conversationId: message.conversationId,
              endUserId: message.endUserId,
              type: message.type,
              tag: `${message.messageId}` || `${message.conversationId}`,
            })
          }
          return logNotificationToAuditLog(notification, api)
        }),
        catchError((error: Error) => {
          if (Notification.permission === 'granted') {
            captureError(error, 'Error receiving notification', {
              notification,
            })
          }
          return empty()
        })
      )
    )
  )
}
